// @ts-check
// Criação de registro no painel lateral: formulário com o identificador, os campos
// obrigatórios e os principais campos visíveis da view padrão. Num campo de relação, o registro que
// ainda não existe é criado num subformulário no próprio painel e volta selecionado, sem perder o resto.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { getIndexView, objectsByNameSingular } from '../../data/metadata-store.js';
import { createRecord } from '../../data/record-store.js';
import { scopeStageOptions, stageFieldKindOf } from '../../data/stage-scope.js';
import {
  FIELD_TYPE,
  getEmptyFieldValue,
  getFieldIcon,
  getLabelIdentifierField,
  getRelationIdFieldName,
  getSortedOptions,
  isFieldReadOnly,
  isManyToOneRelation,
} from '../../lib/field-metadata.js';
import { toAmountMicros } from '../../lib/format.js';
import { Icon } from '../../ui/icon.js';
import { Button, IconButton } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Toggle } from '../../ui/feedback.js';
import { Dropdown } from '../../ui/dropdown.js';
import { RatingStars } from '../object-record/field-display.js';
import { RecordPickerList, toPickedRecord } from '../object-record/record-picker.js';
import { PhoneNumberControl } from '../object-record/phone-input.js';
import { PostcodeAddressControl } from '../object-record/postcode-address-control.js';
import { toAddressApiValue } from '../../lib/address-sub-fields.js';
import { isPostcodeMalformed } from '../../../../shared/address/postcode-autofill.js';
import { DEFAULT_PHONE_COUNTRY_CODE, toPhonesValue } from '../../lib/phone.js';
import { getRelationPreset } from '../../lib/relation-presets.js';
import { closeSidePanel } from './side-panel-state.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

const MAX_FORM_FIELDS = 9;
// Campos que vêm logo depois do identificador, mesmo fora da view padrão: o card da família precisa de
// contato, família e período para nascer completo (o nome é montado a partir deles).
const PRIORITY_FORM_FIELD_NAMES = /** @type {Record<string, string[]>} */ ({
  enrollmentOpportunity: ['pointOfContact', 'household', 'academicPeriod'],
  // toda família que nasce pela tela pede o endereço logo depois do nome, com o CEP primeiro: é o CEP que o mapa usa
  household: ['householdAddress'],
});
// Objetos cujo nome o motor gera quando fica em branco (opportunity-rules.js, buildCardName).
const AUTO_NAMED_OBJECT_NAMES = new Set(['enrollmentOpportunity']);
const FORM_FIELD_TYPES = new Set([
  'TEXT', 'FULL_NAME', 'EMAILS', 'PHONES', 'NUMBER', 'CURRENCY', 'DATE', 'DATE_TIME', 'SELECT',
  'MULTI_SELECT', 'BOOLEAN', 'RATING', 'LINKS', 'RELATION', 'ADDRESS',
]);

/**
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} objectMetadata
 * @param {string[]} [extraPriorityFieldNames]
 */
function getFormFields(objectMetadata, extraPriorityFieldNames = []) {
  const labelField = getLabelIdentifierField(objectMetadata);
  const isCandidate = (/** @type {import('../../lib/field-metadata.js').FieldMetadata} */ field) =>
    FORM_FIELD_TYPES.has(field.type) &&
    !isFieldReadOnly(field) &&
    !field.isSystem &&
    field.isActive !== false &&
    (field.type !== FIELD_TYPE.RELATION || isManyToOneRelation(field));
  const requiredFields = objectMetadata.fields.filter(
    (field) => isCandidate(field) && field.isNullable === false && (field.defaultValue === null || field.defaultValue === undefined),
  );
  const view = getIndexView(objectMetadata.id);
  const viewFields = view
    ? [...view.viewFields]
        .filter((viewField) => viewField.isVisible)
        .sort((first, second) => first.position - second.position)
        .map((viewField) => objectMetadata.fields.find((field) => field.id === viewField.fieldMetadataId))
        .filter(Boolean)
    : objectMetadata.fields;
  /** @type {import('../../lib/field-metadata.js').FieldMetadata[]} */
  const fields = [];
  const priorityFields = [...extraPriorityFieldNames, ...(PRIORITY_FORM_FIELD_NAMES[objectMetadata.nameSingular] ?? [])]
    .map((name) => objectMetadata.fields.find((field) => field.name === name));
  for (const field of [labelField, ...priorityFields, ...requiredFields, ...viewFields]) {
    if (!field || !isCandidate(field) || fields.some((existing) => existing.id === field.id)) continue;
    fields.push(field);
  }
  return fields.slice(0, MAX_FORM_FIELDS);
}

/**
 * Converte o valor do formulário no corpo da API.
 * @param {import('../../lib/field-metadata.js').FieldMetadata} field
 * @param {any} value
 */
export function toApiValue(field, value) {
  if (field.type === FIELD_TYPE.ADDRESS) return toAddressApiValue(value);
  if (field.type === FIELD_TYPE.CURRENCY) {
    if (value === '' || value === null || value === undefined) return undefined;
    return { amountMicros: toAmountMicros(String(value).replace(',', '.')), currencyCode: field.settings?.currencyCode ?? 'BRL' };
  }
  if (field.type === FIELD_TYPE.NUMBER) return value === '' || value === null || value === undefined ? undefined : Number(String(value).replace(',', '.'));
  if (field.type === FIELD_TYPE.DATE_TIME) return value ? new Date(value).toISOString() : undefined;
  if (field.type === FIELD_TYPE.EMAILS) return value ? { primaryEmail: value, additionalEmails: [] } : undefined;
  if (field.type === FIELD_TYPE.LINKS) return value ? { primaryLinkUrl: value, primaryLinkLabel: '', secondaryLinks: [] } : undefined;
  if (field.type === FIELD_TYPE.PHONES) {
    return value?.digits ? toPhonesValue(/** @type {Record<string, any>} */ (getEmptyFieldValue(field)), value.countryCode, value.digits) : undefined;
  }
  if (value === '' || value === null || value === undefined) return undefined;
  return value;
}

/**
 * Valores iniciais do subformulário: o texto buscado vira o identificador do novo registro; família sem texto
 * recebe "Família <sobrenome do contato>" quando o contato já foi escolhido.
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} targetObject
 * @param {string} searchText
 * @param {string} contactLabel nome da pessoa já escolhida no formulário de origem, se houver
 */
export function buildNestedPrefill(targetObject, searchText, contactLabel) {
  const labelField = getLabelIdentifierField(targetObject);
  if (!labelField) return {};
  const text = searchText || (targetObject.nameSingular === 'household' && contactLabel ? `Família ${contactLabel.trim().split(/\s+/).pop()}` : '');
  if (!text) return {};
  if (labelField.type === FIELD_TYPE.FULL_NAME) {
    const [firstName, ...rest] = text.split(/\s+/);
    return { [labelField.name]: { firstName, lastName: rest.join(' ') } };
  }
  return labelField.type === FIELD_TYPE.TEXT ? { [labelField.name]: text } : {};
}

/**
 * @param {{ field: import('../../lib/field-metadata.js').FieldMetadata, value: any, onChange: (value: any) => void, autoFocus?: boolean,
 *   onCreateNew?: (searchText: string) => void, isAutoFilled?: boolean, error?: string | null }} props
 *   isAutoFilled: o motor preenche o campo quando vazio, então ele não é obrigatório no formulário
 *   error: motivo que impede salvar (CEP incompleto)
 */
export function FormField({ field, value, onChange, autoFocus = false, onCreateNew, isAutoFilled = false, error = null }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const focus = (/** @type {HTMLInputElement | null} */ element) => {
    if (autoFocus && element && !element.dataset.focused) {
      element.dataset.focused = 'true';
      requestAnimationFrame(() => element.focus());
    }
  };
  const label = translateMetadataLabel(field.label);
  let control;
  switch (field.type) {
    case FIELD_TYPE.FULL_NAME:
      control = html`<div class="form-field-row">
        <input class="text-input" ref=${focus} placeholder=${t('fieldInput.firstName')} value=${value?.firstName ?? ''}
          onInput=${(event) => onChange({ ...value, firstName: event.currentTarget.value })} />
        <input class="text-input" placeholder=${t('fieldInput.lastName')} value=${value?.lastName ?? ''}
          onInput=${(event) => onChange({ ...value, lastName: event.currentTarget.value })} />
      </div>`;
      break;
    case FIELD_TYPE.SELECT: {
      // card novo só nasce numa etapa que o papel vê (contrato de equipe, seção 3.2)
      const options = scopeStageOptions(field, getSortedOptions(field), 'CREATE');
      const selected = options.find((option) => option.value === value);
      control = html`<button ref=${anchorRef} type="button" class="select-control" onClick=${() => setIsOpen(!isOpen)}>
        ${selected ? html`<${Tag} color=${selected.color} text=${translateMetadataLabel(selected.label)} />` : html`<span class="select-control-placeholder">${t('createRecord.choose')}</span>`}
        <${Icon} name="IconChevronDown" size=${16} />
      </button>
      <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width="anchor">
        <div class="dropdown-menu-items" data-max-height="true">
          ${options.map((option) => html`<div key=${option.value} class="menu-item" role="option" aria-selected=${option.value === value} onClick=${() => {
            onChange(option.value);
            setIsOpen(false);
          }}><span class="menu-item-left"><${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} /></span>
            <span class="menu-item-right">${option.value === value ? html`<${Icon} name="IconCheck" size=${16} />` : null}</span>
          </div>`)}
        </div>
      <//>`;
      break;
    }
    case FIELD_TYPE.MULTI_SELECT: {
      const selectedValues = Array.isArray(value) ? value : [];
      control = html`<div class="form-field-tags">
        ${getSortedOptions(field).map((option) => html`<button
          type="button"
          key=${option.value}
          class="form-field-tag-toggle"
          data-selected=${selectedValues.includes(option.value) ? 'true' : undefined}
          onClick=${() => onChange(selectedValues.includes(option.value) ? selectedValues.filter((item) => item !== option.value) : [...selectedValues, option.value])}
        ><${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} /></button>`)}
      </div>`;
      break;
    }
    case FIELD_TYPE.PHONES:
      control = html`<${PhoneNumberControl}
        draft=${value ?? { countryCode: DEFAULT_PHONE_COUNTRY_CODE, digits: '' }}
        onChange=${onChange}
        autoFocus=${autoFocus}
      />`;
      break;
    case FIELD_TYPE.ADDRESS:
      control = html`<${PostcodeAddressControl} value=${value} onChange=${onChange} autoFocus=${autoFocus} error=${error} />`;
      break;
    case FIELD_TYPE.BOOLEAN:
      control = html`<${Toggle} value=${Boolean(value)} onChange=${onChange} ariaLabel=${label} />`;
      break;
    case FIELD_TYPE.RATING:
      control = html`<${RatingStars} value=${Number(String(value ?? '').replace('RATING_', '')) || 0} onSelect=${(rating) => onChange(rating ? `RATING_${rating}` : null)} />`;
      break;
    case FIELD_TYPE.RELATION: {
      const targetName = field.relation?.targetObjectNameSingular ?? '';
      const preset = getRelationPreset(field);
      control = html`<button ref=${anchorRef} type="button" class="select-control" onClick=${() => setIsOpen(!isOpen)}>
        <span class=${value?.label ? '' : 'select-control-placeholder'}>${value?.label ?? t('createRecord.choose')}</span>
        <${Icon} name="IconChevronDown" size=${16} />
      </button>
      <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width="anchor">
        <${RecordPickerList}
          objectNameSingular=${targetName}
          objectLabel=${preset?.objectLabel}
          selectedIds=${value?.id ? [value.id] : []}
          preselectedRecords=${value?.id ? [value] : []}
          onSelect=${(picked) => {
            onChange(picked);
            setIsOpen(false);
          }}
          onCreateNew=${onCreateNew
            ? (/** @type {string} */ searchText) => {
                setIsOpen(false);
                onCreateNew(searchText);
              }
            : undefined}
        />
      <//>`;
      break;
    }
    default: {
      const inputType = { DATE: 'date', DATE_TIME: 'datetime-local', EMAILS: 'email', LINKS: 'url', PHONES: 'tel' }[field.type] ?? 'text';
      const inputMode = field.type === FIELD_TYPE.NUMBER || field.type === FIELD_TYPE.CURRENCY ? 'decimal' : undefined;
      control = html`<input class="text-input" type=${inputType} inputmode=${inputMode} ref=${focus} placeholder=${isAutoFilled ? t('createRecord.autoName') : label} value=${value ?? ''}
        onInput=${(event) => onChange(event.currentTarget.value)} />`;
    }
  }
  return html`<div class="form-field">
    <label class="form-field-label">
      <${Icon} name=${getFieldIcon(field)} size=${14} />
      <span>${label}</span>
      ${field.isNullable === false && !isAutoFilled ? html`<span class="form-field-required" aria-hidden="true">*</span>` : null}
    </label>
    ${control}
  </div>`;
}

/**
 * Papel restrito por etapa cuja etapa padrão do card ele não vê: o card novo começa na primeira etapa de Ver, em vez de
 * o servidor recusar a criação (contrato de equipe, seção 3.2).
 * @param {import('../../lib/field-metadata.js').ObjectMetadata | undefined} objectMetadata @param {Record<string, unknown>} prefill
 */
function scopedInitialStage(objectMetadata, prefill) {
  const stageField = objectMetadata?.fields.find((field) => field.name === 'stage');
  if (!stageField || prefill.stage !== undefined || stageFieldKindOf(stageField) !== 'CARD') return {};
  const all = getSortedOptions(stageField);
  const allowed = scopeStageOptions(stageField, all, 'CREATE');
  if (allowed.length === all.length) return {};
  const defaultValue = typeof stageField.defaultValue === 'string' ? stageField.defaultValue.replace(/^'|'$/g, '') : null;
  if (defaultValue && allowed.some((option) => option.value === defaultValue)) return {};
  return allowed[0] ? { stage: allowed[0].value } : {};
}

/**
 * @param {{ objectNameSingular: string, prefill?: Record<string, unknown>, onCreated?: (record: any) => void,
 *   parentObjectLabel?: string, onBack?: () => void, priorityFieldNames?: string[], objectLabel?: string }} props
 *   parentObjectLabel e onBack: subformulário aberto a partir de um campo de relação de outro formulário
 */
export function CreateRecordPanel({ objectNameSingular, prefill = {}, onCreated, parentObjectLabel, onBack, priorityFieldNames = [], objectLabel: objectLabelOverride }) {
  const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
  const [values, setValues] = useState(/** @type {Record<string, any>} */ (() => ({ ...scopedInitialStage(objectMetadata, prefill), ...prefill })));
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState(/** @type {Record<string, string>} */ ({}));
  const [nested, setNested] = useState(/** @type {{ fieldName: string, targetName: string, prefill: Record<string, unknown>, objectLabel?: string } | null} */ (null));
  if (!objectMetadata) return null;
  const fields = getFormFields(objectMetadata, priorityFieldNames);
  const isNested = Boolean(onBack);
  const objectLabel = objectLabelOverride ?? translateMetadataLabel(objectMetadata.labelSingular);

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    // CEP pela metade não vai: o mapa não localizaria a família (contrato 07, seção 9.9)
    const errors = Object.fromEntries(fields
      .filter((field) => field.type === FIELD_TYPE.ADDRESS && isPostcodeMalformed(values[field.name]?.addressPostcode))
      .map((field) => [field.name, t('familyPostcode.form.incomplete')]));
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    /** @type {Record<string, unknown>} */
    const data = {};
    for (const [name, value] of Object.entries(values)) {
      const field = objectMetadata.fields.find((candidate) => candidate.name === name);
      if (!field) {
        data[name] = value;
        continue;
      }
      if (isManyToOneRelation(field)) {
        if (value?.id) data[getRelationIdFieldName(field)] = value.id;
        continue;
      }
      const apiValue = toApiValue(field, value);
      if (apiValue !== undefined) data[name] = apiValue;
    }
    setIsSaving(true);
    try {
      const record = await createRecord(objectMetadata, data);
      showSuccessSnackbar(t('createRecord.created', { object: objectLabel }));
      if (!isNested) closeSidePanel();
      onCreated?.(record);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  /** @param {import('../../lib/field-metadata.js').FieldMetadata} field */
  const createNewFor = (field) => {
    const targetName = field.relation?.targetObjectNameSingular;
    const targetObject = targetName ? objectsByNameSingular.value.get(targetName) : undefined;
    if (!targetName || !targetObject) return undefined;
    const contactField = objectMetadata.fields.find(
      (candidate) => candidate.type === FIELD_TYPE.RELATION && candidate.relation?.targetObjectNameSingular === 'person' && values[candidate.name]?.label,
    );
    const contactLabel = contactField ? values[contactField.name].label : '';
    const preset = getRelationPreset(field);
    return (/** @type {string} */ searchText) =>
      setNested({
        fieldName: field.name,
        targetName,
        prefill: { ...(preset?.prefill ?? {}), ...buildNestedPrefill(targetObject, searchText, contactLabel) },
        objectLabel: preset?.objectLabel,
      });
  };

  const nestedTarget = nested;
  const nestedPanel = nestedTarget
    ? html`<${CreateRecordPanel}
        key=${`${nestedTarget.fieldName}:${nestedTarget.targetName}`}
        objectNameSingular=${nestedTarget.targetName}
        prefill=${nestedTarget.prefill}
        objectLabel=${nestedTarget.objectLabel}
        parentObjectLabel=${objectLabel}
        onBack=${() => setNested(null)}
        onCreated=${(/** @type {any} */ record) => {
          const picked = toPickedRecord(record, nestedTarget.targetName);
          setValues((current) => ({ ...current, [nestedTarget.fieldName]: picked }));
          setNested(null);
        }}
      />`
    : null;

  // O formulário de origem fica montado (só escondido) para manter o que já foi preenchido.
  return html`<form class="side-panel-page" onSubmit=${submit} hidden=${Boolean(nested)}>
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        ${isNested
          ? html`<${IconButton} icon="IconArrowLeft" size="small" variant="tertiary" ariaLabel=${t('createRecord.back', { object: parentObjectLabel ?? '' })} onClick=${onBack} />`
          : null}
        <span class="side-panel-page-chip">
          <${Icon} name=${objectMetadata.icon ?? 'IconCube'} size=${16} />
          <span>${t('createRecord.title', { object: objectLabel })}</span>
        </span>
      </div>
      <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
    </div>
    <div class="side-panel-content create-record-form">
      ${isNested ? html`<p class="create-record-hint">${t('createRecord.willReturn', { object: parentObjectLabel ?? '' })}</p>` : null}
      ${fields.map((field, index) => html`<${FormField}
        key=${field.id}
        field=${field}
        value=${values[field.name]}
        autoFocus=${index === 0}
        onChange=${(value) => {
          setValues((current) => ({ ...current, [field.name]: value }));
          if (fieldErrors[field.name]) setFieldErrors(({ [field.name]: _removed, ...rest }) => rest);
        }}
        error=${fieldErrors[field.name] ?? null}
        onCreateNew=${field.type === FIELD_TYPE.RELATION ? createNewFor(field) : undefined}
        isAutoFilled=${index === 0 && AUTO_NAMED_OBJECT_NAMES.has(objectMetadata.nameSingular)}
      />`)}
    </div>
    <div class="side-panel-footer">
      <${Button} title=${isNested ? t('createRecord.back', { object: parentObjectLabel ?? '' }) : t('common.cancel')} variant="secondary" size="small" onClick=${onBack ?? closeSidePanel} />
      <${Button} title=${t('createRecord.submit')} accent="blue" size="small" type="submit" isLoading=${isSaving} hotkeyLabel="⏎" />
    </div>
  </form>${nestedPanel}`;
}
